import React, { useState } from 'react'
import { Form, Alert, ProgressBar } from 'react-bootstrap'
import { replaceDocument } from '../../services/documentService'
import { FileDragDrop } from '../FileDragDrop'
import { Modal } from '../UI/Modal/Modal'
import { useForm, FormProvider } from 'react-hook-form'
import type { Document } from '../../types/documents'
import { AlertCircle } from 'lucide-react'

const ALLOWED_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
]

const MAX_FILE_SIZE = 26214400 // 25MB

interface ReplaceDocumentModalProps {
  document: Document
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface FormValues {
  name: string
}

export const ReplaceDocumentModal: React.FC<ReplaceDocumentModalProps> = ({
  document,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      name: document.name,
    },
  })

  const [file, setFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const calculateStorageDelta = (): {
    delta: number
    formatted: string
    sign: string
  } | null => {
    if (!file) return null
    const delta = file.size - document.size
    const absDelta = Math.abs(delta)
    return {
      delta,
      formatted: formatBytes(absDelta),
      sign: delta >= 0 ? '+' : '-',
    }
  }

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File is too large. Maximum size is 25MB.`
    }

    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`
    }

    return null
  }

  const handleFileChange = (selectedFile: File | null) => {
    if (!selectedFile) {
      setFile(null)
      setError(null)
      return
    }

    const validationError = validateFile(selectedFile)
    if (validationError) {
      setError(validationError)
      setFile(null)
      return
    }

    setFile(selectedFile)
    setError(null)
  }

  const handleClose = () => {
    setFile(null)
    methods.reset()
    setUploadProgress(0)
    setError(null)
    onClose()
  }

  const handleReplace = async () => {
    if (!file) return

    setIsUploading(true)
    setError(null)
    setUploadProgress(0)

    try {
      const formValues = methods.getValues()
      const newName = formValues.name?.trim()

      await replaceDocument(
        document.id,
        {
          file,
          name: newName !== document.name ? newName : undefined,
        },
        (progress) => {
          setUploadProgress(progress)
        }
      )

      onSuccess?.()
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Replace failed')
    } finally {
      setIsUploading(false)
    }
  }

  const storageDelta = calculateStorageDelta()

  return (
    <FormProvider {...methods}>
      <Modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose()
          }
        }}
        size="lg"
        title="Replace Document"
        onAccept={handleReplace}
        acceptButtonText="Replace"
        blockAccept={!file || isUploading}
        loading={isUploading}
        onCancel={handleClose}
        cancelButtonText="Cancel"
      >
        <div className="space-y-4">
          {/* Current Document Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">
              Current Document
            </h3>
            <div className="space-y-1 text-sm">
              <div>
                <span className="text-gray-600">Name:</span>{' '}
                <span className="text-gray-900">{document.name}</span>
              </div>
              <div>
                <span className="text-gray-600">Version:</span>{' '}
                <span className="text-gray-900">
                  v{document.version_number || 1}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Size:</span>{' '}
                <span className="text-gray-900">
                  {formatBytes(document.size)}
                </span>
              </div>
            </div>
          </div>

          {/* Warning Message */}
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle
              className="text-yellow-600 flex-shrink-0 mt-0.5"
              size={16}
            />
            <p className="text-sm text-yellow-800">
              Uploading a new file will create version{' '}
              <strong>v{(document.version_number || 1) + 1}</strong>. The
              current version will no longer be accessible.
            </p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New File
            </label>
            <FileDragDrop
              file={file}
              onFileChange={handleFileChange}
              supportText={`Max 25MB • ${ALLOWED_EXTENSIONS.join(', ')}`}
            />
          </div>

          {/* Storage Delta */}
          {storageDelta && (
            <div className="bg-[#EEF5F2] border border-[#2C6B4E] rounded-lg p-3">
              <div className="text-sm">
                <span className="text-[#06512F] font-medium">
                  Storage Impact:
                </span>{' '}
                <span
                  className={`font-semibold ${
                    storageDelta.delta >= 0 ? 'text-red-600' : 'text-[#087443]'
                  }`}
                >
                  {storageDelta.sign}
                  {storageDelta.formatted}
                </span>
              </div>
            </div>
          )}

          {/* Name Field */}
          <Form.Group>
            <Form.Label>Document Name</Form.Label>
            <Form.Control
              type="text"
              {...methods.register('name')}
              placeholder="Document name"
            />
            <Form.Text className="text-muted">
              You can optionally change the name during replacement
            </Form.Text>
          </Form.Group>

          {/* Progress Bar */}
          {isUploading && (
            <div>
              <ProgressBar
                now={uploadProgress}
                label={`${uploadProgress}%`}
                animated
              />
            </div>
          )}

          {/* Error Display */}
          {error && <Alert variant="danger">{error}</Alert>}
        </div>
      </Modal>
    </FormProvider>
  )
}
